import React, { lazy, Suspense } from "react";
import { ClientGuard, ClientAuthLayout, ClientLayout } from "./portals/client";
import { DesignerGuard, DesignerLayout } from "./portals/designer";
/* Direct files, not the index, so the supplier pages stay in their own chunk. */
import { SupplierGuard } from "./portals/supplier/components/SupplierGuard";
import { SupplierLayout } from "./portals/supplier/components/SupplierLayout";
/* Direct files, not the index, so the partner portal pages stay in their own chunk. */
import { PartnerFence } from "./portals/partner/components/PartnerFence";
import { PartnerGuard } from "./portals/partner/components/PartnerGuard";
import { PartnerLayout } from "./portals/partner/components/PartnerLayout";
import { PARTNER_PORTALS, type FlowPartnerKind } from "@/lib/auth/partnerAccess";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileNamesProvider } from "@/contexts/ProfileNamesContext";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DocumentTitle } from "@/components/DocumentTitle";
import { DashboardShellSkeleton } from "@/components/common/DashboardShellSkeleton";
import { PortalThemeScope } from "@/components/theme/PortalThemeScope";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ThemeContextProvider } from "@/components/theme/ThemeContextProvider";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { startQueryTracking, endQueryTracking } from "@/lib/queryMonitor";

/**
 * Retry dynamic imports once on failure to recover from stale chunk references
 * after a redeploy (e.g. "Failed to fetch dynamically imported module").
 */
const lazyWithRetry = <T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    const reloadKey = `lazy-retry-${factory.toString().slice(0, 64)}`;
    try {
      return await factory();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
          message
        );
      if (isChunkError && typeof window !== "undefined") {
        const alreadyRetried = sessionStorage.getItem(reloadKey);
        if (!alreadyRetried) {
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
          /** Return a never-resolving promise while the page reloads */
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });

/** Lazy load route components for code splitting */
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const InternSignup = lazyWithRetry(() => import("./pages/InternSignup"));
const CheckEmail = lazyWithRetry(() => import("./pages/CheckEmail"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyWithRetry(() => import("./portals/admin/pages/Dashboard"));
/**
 * Every staff route renders through Dashboard. While its chunk loads, show the
 * shell's own skeleton (sidebar rail, top bar, content) rather than the
 * app-wide spinner, so the first frame already looks like the shell.
 */
const StaffDashboard = () => (
  <Suspense fallback={<DashboardShellSkeleton />}>
    <Dashboard />
  </Suspense>
);
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
/** Standalone design-request wizard — its own route, outside the Dashboard shell. */
const DesignRequestWizardPage = lazyWithRetry(
  () => import("./modules/DesignRequests/DesignRequestWizard/pages/DesignRequestWizardPage"),
);
const OrderCreateTypePage = lazyWithRetry(
  () => import("./modules/Orders/pages/OrderCreateTypePage"),
);
const RegularOrderCreatePage = lazyWithRetry(
  () => import("./modules/Orders/pages/RegularOrderCreatePage"),
);
const LinkOrderCreatePage = lazyWithRetry(
  () => import("./modules/Orders/pages/LinkOrderCreatePage"),
);
const OrderSelectDealPage = lazyWithRetry(
  () => import("./modules/Orders/pages/OrderSelectDealPage"),
);
const OrderAddDesignsPage = lazyWithRetry(
  () => import("./modules/Orders/pages/OrderAddDesignsPage"),
);
const OrderBuildPage = lazyWithRetry(
  () => import("./modules/Orders/pages/OrderBuildPage"),
);
const OrderBuildLegacyRedirect = lazyWithRetry(
  () => import("./pages/OrderBuildLegacyRedirect"),
);
const DesignReviewPage = lazyWithRetry(
  () => import("./modules/DesignRequests/DesignReview/pages/DesignReviewPage"),
);
const DesignReviewBulkRevisionPage = lazyWithRetry(
  () => import("./modules/DesignRequests/DesignReview/pages/DesignReviewBulkRevisionPage"),
);

/** Client Portal (separate experience — own auth, own layout, no admin sidebar) */
const ClientLoginPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientLoginPage })),
);
const ClientSignupPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientSignupPage })),
);
const ClientCatalogPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientCatalogPage })),
);
const ClientDesignsPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientMyDesignsPage })),
);
const ClientQuotesPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientMyQuotesPage })),
);
const ClientOrdersPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientMyOrdersPage })),
);
const ClientProfilePage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientMyProfilePage })),
);
const ClientInvoicesPage = lazyWithRetry(() =>
  import("./portals/client").then((m) => ({ default: m.ClientMyInvoicesPage })),
);

/** Designer Portal (staff auth, own nav-drawer shell — mirrors the client portal) */
const DesignerWorkPage = lazyWithRetry(() =>
  import("./portals/designer").then((m) => ({ default: m.DesignWorkPage })),
);
const DesignerWorkDetailPage = lazyWithRetry(() =>
  import("./portals/designer").then((m) => ({ default: m.DesignWorkDetailPage })),
);

/** Supplier Portal (a supplier's own board and orders; own shell) */
const SupplierBoardRoute = lazyWithRetry(() =>
  import("./portals/supplier").then((m) => ({ default: m.SupplierBoardRoute })),
);
const SupplierOrderRoute = lazyWithRetry(() =>
  import("./portals/supplier").then((m) => ({ default: m.SupplierOrderRoute })),
);

/** Printer, FC and warehouse portals (one shell, per-partner config) */
const PartnerBoardRoute = lazyWithRetry(() =>
  import("./portals/partner").then((m) => ({ default: m.PartnerBoardRoute })),
);
const PartnerScheduleRoute = lazyWithRetry(() =>
  import("./portals/partner").then((m) => ({ default: m.PartnerScheduleRoute })),
);
const PartnerOrderRoute = lazyWithRetry(() =>
  import("./portals/partner").then((m) => ({ default: m.PartnerOrderRoute })),
);

/**
 * One portal's routes: `/printer` → `/printer/board`, then the guarded shell.
 * Printer (P1), warehouse (P3) and FC (P4) have an order view; the labels
 * routes land in P6.
 */
function partnerPortalRoutes(partner: FlowPartnerKind) {
  const { root } = PARTNER_PORTALS[partner];
  return [
    <Route key={`${partner}-root`} path={root} element={<Navigate to={`${root}/board`} replace />} />,
    <Route
      key={`${partner}-shell`}
      element={
        <PartnerGuard partner={partner}>
          <PartnerLayout />
        </PartnerGuard>
      }
    >
      <Route path={`${root}/board`} element={<PartnerBoardRoute />} />
      <Route path={`${root}/schedule`} element={<PartnerScheduleRoute />} />
      {partner === "printer" && <Route path={`${root}/orders/:printerOrderId`} element={<PartnerOrderRoute />} />}
      {partner === "warehouse" && <Route path={`${root}/orders/:warehouseOrderId`} element={<PartnerOrderRoute />} />}
      {partner === "fc" && <Route path={`${root}/orders/:fcOrderId`} element={<PartnerOrderRoute />} />}
    </Route>,
  ];
}

/** Configure QueryClient with optimized caching and monitoring */
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onSuccess: (data, query) => {
      /** Track successful queries */
      const fromCache = query.state.dataUpdateCount > 1;
      endQueryTracking(query.queryKey as unknown[], 'success', fromCache);
    },
    onError: (error, query) => {
      /** Track failed queries */
      endQueryTracking(
        query.queryKey as unknown[], 
        'error', 
        false, 
        error instanceof Error ? error.message : 'Unknown error'
      );
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: () => {
      /** Log mutation success for debugging */
      if (import.meta.env.DEV) {
        console.log('✅ Mutation succeeded');
      }
    },
    onError: (error) => {
      console.error('❌ Mutation failed:', error);
    },
  }),
  defaultOptions: {
    queries: {
      /** Data is considered fresh for 5 minutes */
      staleTime: 5 * 60 * 1000,
      /** Cache is kept for 30 minutes */
      gcTime: 30 * 60 * 1000,
      /**
       * Smart retry: don't burn 3 network round-trips on errors that won't
       * succeed on retry. Schema/permission errors are bugs — surface them
       * immediately so they're visible in DevTools. Network-shaped errors
       * (timeouts, connection drops) still get one retry.
       */
      retry: (failureCount, error) => {
        const msg = error instanceof Error ? error.message.toLowerCase() : "";
        const isDeterministicFailure =
          msg.includes("does not exist") ||         /** column / relation missing */
          msg.includes("permission denied") ||      /** RLS / role-based deny */
          msg.includes("violates") ||               /** constraint violation */
          msg.includes("row-level security") ||     /** RLS policy deny */
          msg.includes("invalid input syntax") ||   /** malformed query/args */
          msg.includes("400") ||                    /** generic 4xx */
          msg.includes("404") ||
          msg.includes("422");
        if (isDeterministicFailure) return false;
        return failureCount < 1; /** One retry for transient/network errors */
      },
      /** Don't refetch on window focus (real-time handles updates) */
      refetchOnWindowFocus: false,
      /** Don't refetch on reconnect (real-time handles updates) */
      refetchOnReconnect: false,
    },
  },
});

/** Start tracking queries when they begin fetching */
const originalFetch = queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'added' || (event.type === 'updated' && event.action?.type === 'fetch')) {
    startQueryTracking(event.query.queryKey as unknown[]);
  }
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <ThemeContextProvider>
        <TooltipProvider>
          <Toaster />
          {/* PermissionsProvider is session-self-contained (its own auth
              subscription) so it covers BOTH the admin and client portals
              without depending on AuthProvider or the client useClientAuth hook. */}
          <PermissionsProvider>
          <BrowserRouter>
            <PortalThemeScope />
            <DocumentTitle />
            <AuthProvider>
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin-slow h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
                {/* A partner login (levels 4 to 7) never leaves its own portal: staff screens would only 403. */}
                <PartnerFence>
                <Routes>
                <Route path="/" element={<Index />} />
                {/* Auth routes - using /signin */}
                <Route path="/signin" element={<Auth />} />
                <Route path="/signin/check-email" element={<CheckEmail />} />
                <Route path="/signin/reset-password" element={<ResetPassword />} />
                {/* Legacy redirects for backward compatibility */}
                <Route path="/login" element={<Navigate to="/signin" replace />} />
                <Route path="/login/check-email" element={<Navigate to="/signin/check-email" replace />} />
                <Route path="/login/reset-password" element={<Navigate to="/signin/reset-password" replace />} />
                <Route path="/auth" element={<Navigate to="/signin" replace />} />
                <Route path="/auth/check-email" element={<Navigate to="/signin/check-email" replace />} />
                <Route path="/auth/reset-password" element={<Navigate to="/signin/reset-password" replace />} />
                <Route path="/intern-signup" element={<InternSignup />} />
                <Route path="/design-requests" element={<StaffDashboard />} />
                <Route path="/design-work" element={<StaffDashboard />} />
                <Route
                  path="/design-work/completed/:designRequestId"
                  element={<StaffDashboard />}
                />
                <Route path="/design-work/:designRequestId" element={<StaffDashboard />} />
                <Route path="/access-denied" element={<StaffDashboard />} />
                <Route
                  path="/unauthorized"
                  element={<Navigate to="/access-denied" replace />}
                />
                {/* Renders standalone rather than through Dashboard: the
                    wizard is a full-bleed focused flow, and routing it via
                    Dashboard would mean registering a tab in
                    dashboardModules + MainDashboard. React Router ranks this
                    above the exact "/design-requests" match, so that route is
                    unaffected. */}
                <Route path="/design-requests/create" element={<DesignRequestWizardPage />} />
                <Route path="/orders/create/:orderKind/deal" element={<OrderSelectDealPage />} />
                <Route path="/orders/create/:orderKind/designs" element={<OrderAddDesignsPage />} />
                <Route path="/orders/create/:orderKind/place" element={<OrderBuildPage />} />
                <Route
                  path="/orders/create/:orderKind/build"
                  element={<OrderBuildLegacyRedirect />}
                />
                <Route path="/orders/create/regular" element={<RegularOrderCreatePage />} />
                <Route path="/orders/create/link" element={<LinkOrderCreatePage />} />
                <Route path="/orders/create/store" element={<LinkOrderCreatePage />} />
                <Route path="/orders/create" element={<OrderCreateTypePage />} />
                <Route path="/review/:token/revisions" element={<DesignReviewBulkRevisionPage />} />
                <Route path="/review/:token" element={<DesignReviewPage />} />
                {/* List, detail, and staff revision render inside Dashboard (sidebar + header). */}
                <Route
                  path="/design-requests/:requestId/items/:itemId/revision"
                  element={<StaffDashboard />}
                />
                <Route
                  path="/design-requests/:requestId/revisions"
                  element={<StaffDashboard />}
                />
                <Route path="/design-requests/:requestId" element={<StaffDashboard />} />
                {/* Legacy alias — Design Requests moved to /design-requests */}
                <Route path="/dashboard" element={<Navigate to="/design-requests" replace />} />
                <Route path="/designer-overview" element={<StaffDashboard />} />
                <Route path="/licensing" element={<StaffDashboard />} />
                <Route path="/orders/*" element={<StaffDashboard />} />
                <Route path="/order-management" element={<Navigate to="/orders" replace />} />
                <Route path="/order-pipeline" element={<Navigate to="/orders" replace />} />
                {/* Wildcard so the record detail route (/payments/:recordId)
                    resolves through Dashboard (sidebar + header). */}
                <Route path="/payments/*" element={<StaffDashboard />} />
                {/* Wildcard so the order detail route (/production/orders/:orderId)
                    resolves inside the dashboard shell. */}
                <Route path="/production/*" element={<StaffDashboard />} />
                {/* Wildcard so the supplier order detail route
                    (/supplier-orders/orders/:orderId) resolves inside the
                    dashboard shell. */}
                <Route path="/supplier-orders/*" element={<StaffDashboard />} />
                {/*
                  MUST stay above the /suppliers/* redirect below, which would
                  otherwise swallow it and send this straight to the board.
                */}
                <Route path="/suppliers/performance" element={<StaffDashboard />} />
                {/* The pre-rename order detail path — keep old links working. */}
                <Route path="/suppliers/orders/*" element={<Navigate to="/supplier-orders" replace />} />
                {/* One supplier's board as they see it, and Manage statuses
                    (Habits team only). Also above the redirect below. */}
                <Route path="/suppliers/:supplierId/*" element={<StaffDashboard />} />
                {/* Renamed from /suppliers — keep old links working. */}
                <Route path="/suppliers/*" element={<Navigate to="/supplier-orders" replace />} />
                {/* Printer flow staff boards (holding page until their phase lands). */}
                <Route path="/printer-orders/*" element={<StaffDashboard />} />
                <Route path="/blanks-orders/*" element={<StaffDashboard />} />
                <Route path="/fc-orders/*" element={<StaffDashboard />} />
                <Route path="/warehouse-orders/*" element={<StaffDashboard />} />
                <Route path="/inventory" element={<StaffDashboard />} />
                <Route path="/printers/*" element={<StaffDashboard />} />
                <Route path="/warehouses/*" element={<StaffDashboard />} />
                <Route path="/fulfillment-centers/*" element={<StaffDashboard />} />
                <Route path="/order-overview" element={<StaffDashboard />} />
                <Route path="/sales-pipeline/*" element={<StaffDashboard />} />
                <Route path="/user-management" element={<StaffDashboard />} />
                {/* Legacy — Client Management removed; clients live under Users */}
                <Route path="/client-management" element={<Navigate to="/user-management" replace />} />
                {/* Wildcard so the nested drill-down routes inside the
                    SchoolManagement module (Layer 2/3 + /manage) all
                    resolve through Dashboard. */}
                <Route path="/school-management/*" element={<StaffDashboard />} />
                <Route path="/accounts/*" element={<StaffDashboard />} />
                <Route path="/quote-management" element={<StaffDashboard />} />
                {/* Wildcard so the module's detail route
                    (/quote-requests/:versionId) resolves through Dashboard. */}
                <Route path="/quote-requests/*" element={<StaffDashboard />} />
                {/* Legacy /quoter redirects to the renamed Quote Management page
                    so existing bookmarks / Slack links keep working. */}
                <Route path="/quoter" element={<Navigate to="/quote-management" replace />} />
                {/* Wildcard so the module's nested detail route
                    (/product-catalog/:idOrSlug) resolves through Dashboard. */}
                <Route path="/product-catalog/*" element={<StaffDashboard />} />
                <Route path="/product-configuration" element={<StaffDashboard />} />
                <Route path="/email-configuration" element={<StaffDashboard />} />
                <Route path="/past-activity" element={<StaffDashboard />} />
                <Route path="/theme-editor" element={<StaffDashboard />} />
                <Route path="/usage-tracker" element={<StaffDashboard />} />

                {/* ── Client Portal (separate auth, no admin sidebar) ── */}
                <Route path="/client" element={<Navigate to="/client/catalog" replace />} />
                {/* Login + signup share a persistent layout shell so the
                    right marketing column doesn't re-mount on route swap. */}
                <Route element={<ClientAuthLayout />}>
                  <Route path="/client/login" element={<ClientLoginPage />} />
                  <Route path="/client/signup" element={<ClientSignupPage />} />
                </Route>
                {/* Authenticated client portal — ONE guard + shared layout so
                    the shell/sidebar mounts ONCE; navigation swaps only the
                    <Outlet/> content (no remount / "refresh" flash). */}
                <Route element={<ClientGuard><ClientLayout /></ClientGuard>}>
                  <Route path="/client/catalog" element={<ClientCatalogPage />} />
                  {/* Legacy merged events+orders page — orders now live at
                      /client/orders, events at /client/designs. */}
                  <Route path="/client/proofs" element={<Navigate to="/client/orders" replace />} />
                  <Route path="/client/designs" element={<ClientDesignsPage />} />
                  <Route path="/client/quotes" element={<ClientQuotesPage />} />
                  <Route path="/client/orders" element={<ClientOrdersPage />} />
                  <Route path="/client/profile" element={<ClientProfilePage />} />
                  <Route path="/client/invoices" element={<ClientInvoicesPage />} />
                </Route>

                {/* ── Designer Portal (staff auth, dedicated nav drawer) ──
                    Mirrors the client portal: ONE guard + shared shell so the
                    sidebar mounts once and only the <Outlet/> swaps. Wrapped in
                    AuthProvider + ProfileNamesProvider (same as the admin
                    Dashboard) so the section pages can read the signed-in user. */}
                <Route path="/designer" element={<Navigate to="/designer/work" replace />} />
                <Route
                  element={
                    <ProfileNamesProvider>
                      <DesignerGuard>
                        <DesignerLayout />
                      </DesignerGuard>
                    </ProfileNamesProvider>
                  }
                >
                  <Route path="/designer/work" element={<DesignerWorkPage />} />
                  <Route
                    path="/designer/work/completed/:designRequestId"
                    element={<DesignerWorkDetailPage />}
                  />
                  <Route
                    path="/designer/work/:designRequestId"
                    element={<DesignerWorkDetailPage />}
                  />
                  <Route
                    path="/designer/unclaimed"
                    element={<Navigate to="/designer/work?scope=unclaimed" replace />}
                  />
                  <Route
                    path="/designer/active"
                    element={<Navigate to="/designer/work?scope=active" replace />}
                  />
                  <Route
                    path="/designer/completed"
                    element={<Navigate to="/designer/work?scope=completed" replace />}
                  />
                </Route>

                {/* ── Supplier Portal (a supplier's own orders, own shell) ──
                    Same shape as the designer portal: ONE guard + shared shell,
                    only the <Outlet/> swaps. The guard asks the API which
                    supplier the user belongs to; it never signs anyone out. */}
                <Route path="/supplier" element={<Navigate to="/supplier/board" replace />} />
                <Route
                  element={
                    <SupplierGuard>
                      <SupplierLayout />
                    </SupplierGuard>
                  }
                >
                  <Route path="/supplier/board" element={<SupplierBoardRoute />} />
                  <Route path="/supplier/orders/:supplierOrderId" element={<SupplierOrderRoute />} />
                </Route>

                {/* ── Printer, FC and warehouse portals (own shell) ── */}
                {partnerPortalRoutes("printer")}
                {partnerPortalRoutes("fc")}
                {partnerPortalRoutes("warehouse")}

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
                </Routes>
                </PartnerFence>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
          </PermissionsProvider>
        </TooltipProvider>
      </ThemeContextProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
